// Module singleton — GameWrapper writes, screens read.
//
// MIN_CURTAIN_MS is the floor GameWrapper holds the curtain for. It lives here
// rather than inside GameWrapper because a screen whose entrance is supposed to
// look the same around the table has to know when the room's curtain lifts —
// see WineRatingReveal, which derives its shared reveal instant from it.
// Tracks whether the transition curtain is visible OR still exiting.
// Only flips to false in AnimatePresence onExitComplete (after the exit
// animation fully completes), so callers can safely skip their own entrance
// when the curtain owns the reveal.
//
// 2s, down from 3.5s (UX review, 3 Nov 2026): the floor sat on every curtain,
// and between Join and the first question that repeated beat was most of the
// 70 seconds. A player can also tap the curtain away once the next screen is
// ready (GameWrapper), so the floor is a ceiling on waiting, not a fixed wait.
export const MIN_CURTAIN_MS = 2000;

// How long a curtain is held over a step nobody sees (see isCurtainPassThrough)
// before it gives up and lifts anyway. GameViewMode offers its stalled host a
// Continue button after 10s; the curtain must not sit on top of that button.
export const PASS_THROUGH_HOLD_MAX_MS = 10000;

/**
 * Steps the room moves straight through without a screen of their own.
 *
 * Each of these used to raise its own curtain, and with no copy of its own it
 * said "One moment…" — three of them in a row between the setup screen and the
 * first question. They are never anyone's destination, so they get no curtain
 * of their own: the one already covering the screen stays up and names the real
 * next screen (curtainStepFor).
 *
 *  - game-rules and the legacy ability step: always. The host's phone advances
 *    past them on arrival (stepTypeToRoute returns null for both).
 *  - game-view-mode: when TV mode is off for this room, or when the host
 *    already picked Phones on the setup screen (a game-view-mode-set event with
 *    mode 'phone'), the two cases GameViewMode skips itself. Otherwise it shows
 *    the pick or the pairing gate, so it is a real screen and gets an ordinary
 *    curtain. A TV pick is never a skip.
 *
 * @param {{type?: string}|null} step
 * @param {object} o
 * @param {boolean} o.sessionDataLoaded  tvModeEnabled reads false until then
 * @param {boolean} o.tvModeEnabled
 * @param {boolean} [o.phonePreset]  a game-view-mode-set event with mode
 *   'phone' already exists for this session. Only means anything in a TV room.
 */
export function isCurtainPassThrough(step, { sessionDataLoaded, tvModeEnabled, phonePreset } = {}) {
  const type = step?.type;
  if (type === 'game-rules' || type === 'ability') return true;
  if (type !== 'game-view-mode') return false;
  return Boolean(sessionDataLoaded && (!tvModeEnabled || phonePreset));
}

/**
 * The step the curtain should talk about: the current one, or — while the room
 * is on a pass-through step — the first real step after it. Every pass-through
 * is judged with the same options, so a game-view-mode skip is looked past only
 * when it will itself be skipped.
 *
 * @returns {{step: object|null, index: number}}
 */
export function curtainStepFor(flowSteps, stepIndex, opts) {
  const steps = Array.isArray(flowSteps) ? flowSteps : [];
  const current = steps[stepIndex] ?? null;
  if (!isCurtainPassThrough(current, opts)) return { step: current, index: stepIndex };
  for (let i = stepIndex + 1; i < steps.length; i += 1) {
    if (!isCurtainPassThrough(steps[i], opts)) return { step: steps[i], index: i };
  }
  // Nothing real after it: say what it is rather than nothing.
  return { step: current, index: stepIndex };
}

// ── The setup run, as every player but the host sees it ────────────────────
//
// On all three of these steps a player's phone shows the one still line
// "<host> is setting up the game…" (ParticipationMode, GameViewMode; game-rules
// has no screen, so the last one stays). A curtain between them flipped that
// line to green and back: a replay of an 8-player game on 4 Nov 2026 showed
// the setup line, "Where the game shows", the setup line, "Wine 1 coming up"
// twice, the setup line, then the wine intro. For a player they are one screen.
export const SETUP_STEP_TYPES = ['participation-mode', 'game-view-mode', 'game-rules'];

/** @param {{type?: string}|null} step */
export function isSetupStep(step) {
  return SETUP_STEP_TYPES.includes(step?.type);
}

/**
 * The room is moving from one setup step to another: this step and the one
 * before it are both setup steps. GameWrapper raises no curtain for a player
 * here. Entering the run (its first step) still gets one.
 */
export function continuesSetupRun(flowSteps, stepIndex) {
  const steps = Array.isArray(flowSteps) ? flowSteps : [];
  if (!(stepIndex > 0)) return false;
  return isSetupStep(steps[stepIndex]) && isSetupStep(steps[stepIndex - 1]);
}

// ── Is this phone coming BACK to the room, or arriving? ────────────────────
//
// Only wording rides on it, but it is the difference between the truth and
// telling a player who just typed the code that their connection dropped
// ("Getting back to the room… Your answers are safe" on the normal path, UX
// review 3 Nov 2026). A stored seat alone cannot tell them apart: every player
// has one from the moment Join succeeds.
//
// What can: how this document was opened. The URL the browser loaded (the
// navigation timing entry keeps it for the life of the page, whatever the SPA
// pushes afterwards) is /game?code=… only for a reload or a link straight into
// the game. Walking in through Join and the lobby loads some other URL and
// reaches /game in-app.
function readDocumentEntry() {
  try {
    if (typeof window === 'undefined') return null;
    const nav = typeof performance !== 'undefined' && performance.getEntriesByType
      ? performance.getEntriesByType('navigation')?.[0]
      : null;
    const url = new URL(nav?.name || window.location.href, window.location.origin);
    return { pathname: url.pathname, code: url.searchParams.get('code') };
  } catch {
    return null;
  }
}

let documentEntry = readDocumentEntry();
// Rooms this page has already been in, past the "where is the room" wait. A
// second GameWrapper for one of them (the error boundary's Try again, or the
// provider re-entering a room) is a return, even without a reload.
const roomsSeenThisPage = new Set();

const sameCode = (a, b) => Boolean(a && b) && String(a).trim().toUpperCase() === String(b).trim().toUpperCase();

/** This page has been in `code` and got past hydration (GameWrapper marks it). */
export function markRoomSeen(code) {
  if (code) roomsSeenThisPage.add(String(code).trim().toUpperCase());
}

/**
 * @param {object} o
 * @param {string|null} o.sessionCode
 * @param {boolean} o.hasStoredSeat  playerStorage has a player for this code
 * @returns {boolean} true only for a real return to the room
 */
export function isRealRejoin({ sessionCode, hasStoredSeat }) {
  if (!sessionCode) return false;
  if (roomsSeenThisPage.has(String(sessionCode).trim().toUpperCase())) return true;
  if (!hasStoredSeat || !documentEntry) return false;
  const loadedIntoGame = documentEntry.pathname === '/game' || documentEntry.pathname.startsWith('/game/');
  return loadedIntoGame && sameCode(documentEntry.code, sessionCode);
}

/** Tests only: pretend the document was opened at `href` and forget every room. */
export function __resetRejoinStateForTests(href) {
  roomsSeenThisPage.clear();
  try {
    const url = new URL(href, 'http://localhost');
    documentEntry = { pathname: url.pathname, code: url.searchParams.get('code') };
  } catch {
    documentEntry = null;
  }
}

const state = { visible: false };
const listeners = new Set();
export const setCurtainVisible = (v) => {
  if (state.visible === v) return;
  state.visible = v;
  listeners.forEach((fn) => fn(v));
};
export const isCurtainVisible = () => state.visible;
// Subscribe to visibility changes (useSyncExternalStore-compatible).
// Returns an unsubscribe fn.
export const subscribeCurtain = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/**
 * When a screen should start its entrance, so the room starts it together AND
 * the player actually sees it.
 *
 * Three answers, because there are three different reasons to wait:
 *
 *  'await-curtain' — the curtain is on screen. It counts as visible until
 *    `onExitComplete`, so this covers the 440ms while it is exiting, and that
 *    window is the trap: `game-route-visible` fires at the START of the exit, so
 *    a screen that starts on it plays its entrance behind a curtain still on top
 *    of it. That is the 2026-07-14 QA regression — the heading and group card
 *    were already settled by the time the player could see them. Nothing starts
 *    until the curtain is gone. Caller re-asks on `curtain-exited`.
 *  'wait'          — nothing is covering the screen and the room's instant is
 *    still ahead. Hold for `waitMs`, then play the animation IN FULL. Waiting is
 *    what keeps it whole; seeking or truncating would not.
 *  'run'           — start now, also in full. A late screen plays late rather
 *    than skipping a beat the table has finished watching.
 *
 * The wait is capped because an uncovered screen holding its cards at opacity 0
 * is just a blank screen: sync is worth having only while the player cannot tell
 * it is happening.
 *
 * @param {object}  o
 * @param {number}  o.stepEnteredAtMs  when THIS device entered the step
 * @param {number}  o.nowMs
 * @param {boolean} o.curtainUp        is the curtain on screen or still exiting
 * @param {number}  o.startOffsetMs    the room's reveal instant, after step entry
 * @param {number}  o.uncoveredCapMs   longest wait allowed with nothing on top
 * @returns {{action: 'await-curtain'|'wait'|'run', waitMs: number}}
 */
export function revealStartPlan({
  stepEnteredAtMs,
  nowMs,
  curtainUp,
  startOffsetMs,
  uncoveredCapMs,
}) {
  if (curtainUp) return { action: 'await-curtain', waitMs: 0 };
  if (!Number.isFinite(stepEnteredAtMs) || !Number.isFinite(nowMs)) {
    return { action: 'run', waitMs: 0 };
  }
  const remaining = stepEnteredAtMs + startOffsetMs - nowMs;
  if (!(remaining > 0)) return { action: 'run', waitMs: 0 };
  return { action: 'wait', waitMs: Math.min(remaining, uncoveredCapMs) };
}
